import { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TablePagination,
	TextField,
	Button,
	Typography,
	Box,
	Alert,
	LinearProgress,
	Select,
	MenuItem,
	Grid,
	IconButton,
	Divider,
	Tooltip,
	Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import HeaderReport from "../../components/header/report";
import { getStatus, fetchTemplates, sendEvolutionTemplate } from "./req/reqOfficial";
import Footer from "../../components/footer";
import reqEmpresaCredenciais from "../../services/reqMultiTenant/reqEmpresaCredenciais";
import reqWhatsapp from "../../services/reqMultiTenant/reqWhatsApp";
import { toast, ToastContainer } from "react-toastify";
import UploadPhotoV2Image from "../../components/uploading/uploadingPhotoV2image";

const WhatsAppSenderOfficial = () => {
	const [contacts, setContacts] = useState([]);

	// Estado para configuração do Template
	const [templateName, setTemplateName] = useState("");
	const [templateLang, setTemplateLang] = useState("pt_BR");
	const [availableTemplates, setAvailableTemplates] = useState([]);

	// Variáveis do template: lista de objetos { id, type: 'column' | 'manual', value: '' }
	const [templateVars, setTemplateVars] = useState([]);
	const [imagePreview, setImagePreview] = useState(null);

	const [status, setStatus] = useState("");
	const [loading, setLoading] = useState(true);
	const [isSending, setIsSending] = useState(false);
	const [fields, setFields] = useState([]); // Colunas detectadas do Excel
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);

	// Controles de envio
	const [batchSize, setBatchSize] = useState(9);
	const [batchPauseMin, setBatchPauseMin] = useState(4);
	const [delayMin, setDelayMin] = useState(36);
	const [delayMax, setDelayMax] = useState(62);

	const [errorLimit, setErrorLimit] = useState(500);

	const [instanciaWhatsapp, setInstanciaWhatsapp] = useState([]);
	const [credentials, setCredentials] = useState([]);
	const [selectedInstance, setSelectedInstance] = useState(null);
	const [isWaiting, setIsWaiting] = useState(false);

	const cancelRef = useRef(false);

	const STATUS_CYCLE = ["📤 Pendente Envio", "🚫 Não enviar", "✅ Enviado"];

	useEffect(() => {
		fetchCredentials();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (instanciaWhatsapp.length > 0 && !selectedInstance) {
			const firstWithPhone = instanciaWhatsapp.find((instance) => instance?.telefone);
			setSelectedInstance(firstWithPhone || instanciaWhatsapp[0]);
		}
	}, [instanciaWhatsapp, selectedInstance]);

	useEffect(() => {
		if (selectedInstance && credentials) {
			loadTemplates();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedInstance, credentials]);

	const loadTemplates = async (manual = false) => {
		try {
			if (!selectedInstance) {
				if (manual) toast.warning("Selecione uma instância primeiro.");
				return;
			}

			const cred = await getCredentialsForInstance(selectedInstance);
			const templates = await fetchTemplates(cred.url, cred.apiKey, cred.instance);

			const list = Array.isArray(templates) ? templates : templates?.data || [];
			setAvailableTemplates(list);
			if (manual) toast.success("Templates atualizados!");
		} catch (error) {
			console.error("Erro ao carregar templates:", error);
			if (manual) toast.error("Erro ao buscar templates.");
		}
	};

	// =========================
	// Helpers
	// =========================

	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	const getRandomDelay = (min = delayMin, max = delayMax) => {
		const minVal = Math.max(3, Number(min));
		const maxVal = Math.max(5, Number(max));
		const finalMin = minVal;
		const finalMax = maxVal < minVal ? minVal : maxVal;
		return Math.floor(Math.random() * (finalMax - finalMin + 1) + finalMin) * 1000;
	};

	const getPhoneFieldName = (contact) => {
		return Object.keys(contact).find((key) => key.toLowerCase().includes("telefone")) || "telefone";
	};

	const getCredObject = () => {
		if (!credentials) return null;
		if (Array.isArray(credentials)) return credentials[0] || null;
		return credentials;
	};

	const normalize = (v) =>
		String(v ?? "")
			.trim()
			.toUpperCase();

	const isConnectedStatus = (st) => {
		const raw = st?.status ?? st?.state ?? st?.instance?.status ?? st?.instance?.state;
		const s = normalize(raw);
		const CONNECTED_VALUES = new Set([
			"CONNECTED",
			"CONNECTION_OPEN",
			"OPEN",
			"ONLINE",
			"READY",
			"ACTIVE",
			"TRUE",
			"1",
		]);
		const DISCONNECTED_VALUES = new Set([
			"DISCONNECTED",
			"CLOSED",
			"OFFLINE",
			"NOT_FOUND",
			"FALSE",
			"0",
			"UNPAIRED",
		]);

		const boolConnected = st?.connected === true || st?.isConnected === true;
		if (boolConnected) return true;
		if (!s) return false;
		if (DISCONNECTED_VALUES.has(s)) return false;
		if (CONNECTED_VALUES.has(s)) return true;
		return true;
	};

	const getCredentialsForInstance = async (instanceObj) => {
		try {
			const credObj = getCredObject();
			const apiKey = credObj?.api_key;
			const url = credObj?.url;
			const instance = instanceObj?.descricao;

			if (!apiKey || !url) throw new Error("Credenciais não carregadas.");
			if (!instance) throw new Error("Nenhuma instância definida.");

			const statusData = await getStatus(url, apiKey, instance);
			if (!isConnectedStatus(statusData)) {
				throw new Error(`A instância "${instance}" não está conectada.`);
			}

			return { apiKey, url, instance };
		} catch (error) {
			console.error("Erro ao obter credenciais:", error);
			throw error;
		}
	};

	const handleOpenUploadComponent = (e) => {
		e.preventDefault();
		const event = new CustomEvent("upload-whatsapp");
		window.dispatchEvent(event);
	};

	// =========================
	// Data fetch & Upload
	// =========================

	const fetchCredentials = async () => {
		try {
			setLoading(true);
			const [empresaCredenciaisRes, whatsappRes] = await Promise.all([
				reqEmpresaCredenciais.listId("whatsapp"),
				reqWhatsapp.listPaginate("descricao", "", "SIM", 1),
			]);
			setCredentials(empresaCredenciaisRes.data);
			setInstanciaWhatsapp(whatsappRes.data);
		} catch (error) {
			toast.warning(error?.message || "Erro ao buscar credenciais");
		} finally {
			setLoading(false);
		}
	};

	const handleFileUpload = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setLoading(true);
		const reader = new FileReader();

		reader.onload = (event) => {
			try {
				const data = new Uint8Array(event.target.result);
				const workbook = XLSX.read(data, { type: "array" });
				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];
				const json = XLSX.utils.sheet_to_json(worksheet);

				const formatted = json.map((row) => ({
					...row,
					status: row.status || "📤 Pendente Envio",
				}));

				setContacts(formatted);
				setStatus(`Carregado ${formatted.length} contatos.`);

				if (formatted.length > 0) {
					const detectedFields = Object.keys(formatted[0]).filter((key) => key.toLowerCase() !== "status");
					setFields(detectedFields);
				}
			} catch (err) {
				toast.error("Erro ao ler a planilha.");
				console.error(err);
			}
		};

		reader.onloadend = () => {
			setLoading(false);
			e.target.value = null;
		};

		reader.readAsArrayBuffer(file);
	};

	const handleChange = (index, field, value) => {
		const updated = [...contacts];
		updated[index][field] = value;
		setContacts(updated);
	};

	const toggleStatus = (index) => {
		const updated = [...contacts];
		const current = updated[index].status;
		const nextStatus = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
		updated[index].status = nextStatus;
		setContacts(updated);
	};

	// =========================
	// Template Config Logic
	// =========================

	const addVariable = () => {
		if (contacts.length === 0) {
			toast.warning("Carregue a planilha de contatos antes de adicionar variáveis.");
			return;
		}
		setTemplateVars([...templateVars, { id: Date.now(), type: "column", value: "" }]);
	};

	const removeVariable = (index) => {
		const updated = [...templateVars];
		updated.splice(index, 1);
		setTemplateVars(updated);
	};

	const updateVariable = (index, field, newValue) => {
		const updated = [...templateVars];
		updated[index][field] = newValue;
		// Reset value if type changes to prevent inconsistencies
		if (field === "type") updated[index].value = "";
		setTemplateVars(updated);
	};

	// Helper to extract clean variable values for a specific contact
	const buildVariablesForContact = (contact) => {
		return templateVars.map((v) => {
			if (v.type === "column") {
				// Se a coluna não existir no contato, retorna vazio para não quebrar
				return contact[v.value] !== undefined && contact[v.value] !== null ? contact[v.value] : "";
			} else {
				return v.value || ""; // Valor manual estático
			}
		});
	};

	// =========================
	// Sending Logic (TEMPLATE)
	// =========================

	const cancelSending = () => {
		cancelRef.current = true;
		setStatus("Pausa envio...");
	};

	const sendMessages = async () => {
		let countError = 0;

		try {
			setIsSending(true);

			if (!templateName.trim()) {
				toast.warning("Por favor, defina o nome do template.");
				return;
			}

			if (contacts.length === 0) {
				toast.warning("Por favor, carregue contatos.");
				return;
			}

			const credObj = getCredObject();
			if (!credObj?.api_key || !credObj?.url) {
				toast.error("Credenciais não carregadas.");
				return;
			}

			// Setup Instance
			if (!selectedInstance) {
				toast.warning("Selecione uma instância.");
				return;
			}

			setStatus("🚀 Iniciando envio (API Oficial)...");
			cancelRef.current = false;

			const updatedContacts = [...contacts];
			let sentInCurrentBatch = 0;

			for (let i = 0; i < updatedContacts.length; i++) {
				if (countError >= Number(errorLimit)) {
					setStatus("❌ Muitos erros consecutivos. Envio interrompido.");
					break;
				}
				if (cancelRef.current) {
					setStatus("⛔ Envio cancelado pelo usuário.");
					break;
				}

				const contact = updatedContacts[i];
				if (contact.status !== "📤 Pendente Envio") continue;

				// Usa sempre a instância selecionada
				const instanceToUse = selectedInstance;

				let cred;
				try {
					cred = await getCredentialsForInstance(instanceToUse);
				} catch (e) {
					updatedContacts[i].status = "❌ Falhou";
					countError++;
					setContacts([...updatedContacts]);
					// Se falhar a única instância, para tudo?
					// Não necessariamente, pode ser um erro temporário, mas se desconectar, vai falhar todas.
					// Vamos logar e continuar tentando se não atingiu limite de erros.
					continue;
				}

				const phoneField = getPhoneFieldName(contact);
				const variablesToSend = buildVariablesForContact(contact);

				try {
					// Envio via Evolution API
					await sendEvolutionTemplate(
						cred.url,
						cred.apiKey,
						cred.instance,
						contact[phoneField],
						templateName,
						templateLang,
						variablesToSend,
						imagePreview,
					);

					setStatus(
						`📨 Template enviado para ${contact.nome || contact[phoneField]} via ${cred.instance}...`,
					);
					updatedContacts[i].status = "✅ Enviado";
					countError = 0;
					countError = 0;
				} catch (error) {
					console.error("Erro envio:", error);

					// Verifica se é erro crítico de configuração
					// (Template inexistente, Parâmetros errados, Conta bloqueada/banida, Pagamento, Acesso negado)
					const criticalCodes = [
						132001,
						132012,
						132016,
						132018,
						132000,
						132015, // Template
						131008,
						131051,
						100, // Validação/Tipo
						368,
						131031, // Bloqueios
						131042,
						133010, // Billing/Conta
						1005,
						190, // Acesso/Auth
					];
					const errCode = error.code ? Number(error.code) : null;

					if (errCode && criticalCodes.includes(errCode)) {
						toast.error(`Envio interrompido! Erro Crítico: ${error.message}`);
						setStatus(`⛔ Interrompido: ${error.message}`);

						// Marca o atual como falha e para o loop
						updatedContacts[i].status = "❌ Falhou";
						updatedContacts[i].errorDetail = error.message;
						setContacts([...updatedContacts]);
						setIsSending(false);
						return; // Sai da função sendMessages
					}

					const errorMsg = error?.response?.data?.message || error?.message || "Erro desconhecido";
					updatedContacts[i].status = "❌ Falhou";
					updatedContacts[i].errorDetail =
						typeof errorMsg === "object" ? JSON.stringify(errorMsg) : String(errorMsg);
					countError++;
				}

				setContacts([...updatedContacts]);

				// Batch Pause Logic
				if (i < updatedContacts.length - 1) {
					sentInCurrentBatch++;
					const needsBatchPause =
						Number(batchSize) > 0 && Number(batchPauseMin) > 0 && sentInCurrentBatch >= Number(batchSize);

					if (needsBatchPause) {
						const pauseMs = Number(batchPauseMin) * 60 * 1000;
						setIsWaiting(true);
						const startedAt = Date.now();
						let remaining = pauseMs;

						while (remaining > 0) {
							if (cancelRef.current) {
								setStatus("⛔ Cancelado durante pausa.");
								setIsWaiting(false);
								return; // sair da função
							}
							const mins = Math.floor(remaining / 60000);
							const secs = Math.ceil((remaining % 60000) / 1000);
							setStatus(`⏸️ Pausa automática: retoma em ${mins}m ${secs}s...`);
							await delay(1000);
							remaining = pauseMs - (Date.now() - startedAt);
						}
						setIsWaiting(false);
						sentInCurrentBatch = 0;
					} else {
						const waitTime = getRandomDelay();
						setIsWaiting(true);
						setStatus(`Aguardando ${waitTime / 1000}s...`);
						await delay(waitTime);
						setIsWaiting(false);
					}
				}
			}
			setStatus("✅ Envio finalizado.");
		} catch (error) {
			toast.error(error?.message || "Erro crítico ao enviar");
			setStatus(`❌ Erro: ${error?.message}`);
		} finally {
			setIsSending(false);
			setIsWaiting(false);
		}
	};

	// =========================
	// UI
	// =========================

	return (
		<>
			<ToastContainer />
			<div className="mt-page">
				<Box maxWidth="lg" mx="auto" p={2}>
					<HeaderReport title={"WhatsApp Oficial (API Cloud)"} subTitle={"Disparo em massa via Templates"} />

					{/* Instância e Upload */}
					<Grid container spacing={3} mb={3}>
						<Grid item xs={12}>
							<Box className="border p-4 rounded shadow-sm bg-white">
								<Typography variant="h6" gutterBottom color="primary">
									1. Configuração de Instância
								</Typography>
								<Box mb={2}>
									<select
										value={selectedInstance ? JSON.stringify(selectedInstance) : ""}
										onChange={(e) =>
											setSelectedInstance(e.target.value ? JSON.parse(e.target.value) : null)
										}
										className="border rounded p-2 w-full"
										disabled={isSending}
									>
										<option value="">Selecione a instância</option>
										{instanciaWhatsapp.filter(Boolean).map((instance, idx) => (
											<option key={idx} value={JSON.stringify(instance)}>
												{instance.descricao} - {instance.telefone || "Sem número"}
											</option>
										))}
									</select>
									{!selectedInstance && (
										<Alert severity="warning" sx={{ mt: 1 }}>
											Selecione uma instância conectada para continuar.
										</Alert>
									)}
									{selectedInstance && (
										<Alert severity="success" sx={{ mt: 1 }}>
											Instância selecionada: {selectedInstance.descricao}
										</Alert>
									)}
								</Box>

								<Divider sx={{ my: 2 }} />

								<Typography variant="h6" gutterBottom color="primary">
									2. Carregar Contatos
								</Typography>
								<input
									type="file"
									accept=".xlsx, .xls"
									onChange={handleFileUpload}
									disabled={isSending}
									className="mb-2 w-full"
								/>
								<Typography variant="body2" color="textSecondary">
									{contacts.length > 0
										? `${contacts.length} contatos carregados.`
										: "Nenhum contato carregado."}
								</Typography>
								{contacts.length === 0 && selectedInstance && (
									<Alert severity="warning" sx={{ mt: 1 }}>
										Carregue uma planilha Excel com os contatos (colunas: nome, telefone, etc.)
									</Alert>
								)}
								{contacts.length > 0 && (
									<Alert severity="success" sx={{ mt: 1 }}>
										{contacts.length} contatos prontos para envio!
									</Alert>
								)}
							</Box>
						</Grid>

						<Grid item xs={12}>
							<Box className="border p-4 rounded shadow-sm bg-white">
								<Typography variant="h6" gutterBottom color="primary">
									3. Configuração do Template
								</Typography>

								<Alert severity="info" sx={{ mb: 2, fontSize: "0.85rem" }}>
									<strong>Instruções:</strong>
									<br />
									1. Insira o <strong>Nome do Template</strong> (exatamente como no Gerenciador da
									Meta).
									<br />
									2. Se o template tiver variáveis (ex: <code>Olá {"{{1}}"}</code>), clique em{" "}
									<strong>Adicionar</strong> e mapeie a coluna do Excel.
									<br />
									3. A ordem das variáveis deve ser a mesma do texto (<code>{"{{1}}"}</code> = 1ª
									variável, etc).
								</Alert>

								{(!templateName || templateName.trim() === "") && (
									<Alert severity="warning" sx={{ mb: 2 }}>
										⚠️ O nome do template é obrigatório.
									</Alert>
								)}

								<Box mb={2} p={2} bgcolor="#f8f9fa" borderRadius={2} border="1px dashed #ced4da">
									<Typography variant="subtitle2" gutterBottom>
										Mídia do Cabeçalho (Opcional)
									</Typography>
									<Box display="flex" alignItems="center" gap={2}>
										{imagePreview ? (
											<Box
												component="img"
												src={imagePreview}
												alt="Preview"
												sx={{
													width: 80,
													height: 80,
													objectFit: "cover",
													borderRadius: 1,
													border: "1px solid #ddd",
												}}
											/>
										) : (
											<Box
												sx={{
													width: 80,
													height: 80,
													bgcolor: "#eee",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													borderRadius: 1,
													color: "#999",
													fontSize: "0.75rem",
													textAlign: "center",
													p: 1,
												}}
											>
												Sem Imagem
											</Box>
										)}
										<Box flex={1}>
											<Button
												variant="contained"
												color={imagePreview ? "warning" : "primary"}
												onClick={handleOpenUploadComponent}
												disabled={isSending}
												size="small"
												startIcon={imagePreview ? <DeleteIcon /> : <AddIcon />}
											>
												{imagePreview ? "Trocar Imagem" : "Enviar Imagem"}
											</Button>
											{imagePreview && (
												<Button
													variant="text"
													color="error"
													size="small"
													onClick={() => setImagePreview(null)}
													sx={{ ml: 1 }}
													disabled={isSending}
												>
													Remover
												</Button>
											)}
										</Box>
									</Box>
								</Box>

								<Grid container spacing={2}>
									<Grid item xs={8}>
										<Box display="flex" gap={1} alignItems="flex-start">
											<Autocomplete
												options={availableTemplates.map((t) => t.name)}
												freeSolo
												value={templateName}
												onChange={(event, newValue) => {
													setTemplateName(newValue);
													// Tenta encontrar o idioma do template selecionado
													const found = availableTemplates.find((t) => t.name === newValue);
													if (found && found.language) {
														setTemplateLang(found.language);
													}
												}}
												onInputChange={(event, newInputValue) => {
													setTemplateName(newInputValue);
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														label="Nome do Template"
														helperText={
															availableTemplates.length > 0
																? "Selecione ou digite o nome"
																: "Digite o nome exato (aprovado na Meta)"
														}
														error={!templateName && contacts.length > 0}
														size="small"
													/>
												)}
												disabled={isSending}
												fullWidth
											/>
											<Tooltip title="Recarregar Templates">
												<span>
													<IconButton
														onClick={() => loadTemplates(true)}
														disabled={isSending || loading}
														color="primary"
														size="small"
														sx={{ mt: 0.5 }}
													>
														<RefreshIcon />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
									</Grid>
									<Grid item xs={4}>
										<TextField
											label="Idioma"
											fullWidth
											value={templateLang}
											onChange={(e) => setTemplateLang(e.target.value)}
											size="small"
											disabled={isSending}
											helperText="Ex: pt_BR"
										/>
									</Grid>
								</Grid>

								<Box mt={3}>
									<Box display="flex" justifyContent="space-between" alignItems="center">
										<Typography variant="subtitle1" fontWeight="bold">
											Mapear Variáveis do Template
										</Typography>
										<Button
											startIcon={<AddIcon />}
											size="small"
											onClick={addVariable}
											disabled={isSending}
											variant="outlined"
										>
											Adicionar Variável
										</Button>
									</Box>

									<Alert severity="info" sx={{ mt: 1, py: 0, fontSize: "0.80rem" }}>
										Adicione as variáveis na ordem do template (<code>{"{{1}}"}</code>,{" "}
										<code>{"{{2}}"}</code>...). Selecione a coluna correspondente na planilha.
									</Alert>

									{templateVars.length === 0 && (
										<Typography
											variant="caption"
											display="block"
											color="textSecondary"
											sx={{ mt: 2, fontStyle: "italic" }}
										>
											Nenhuma variável adicionada. Se o template não tem variáveis, deixe vazio.
										</Typography>
									)}

									<Box sx={{ maxHeight: 200, overflowY: "auto", mt: 2 }}>
										{templateVars.map((v, index) => (
											<Box
												key={v.id}
												display="flex"
												gap={1}
												mb={1}
												alignItems="center"
												p={1}
												bgcolor="#fff"
												border="1px solid #eee"
												borderRadius={1}
											>
												<Box
													sx={{
														minWidth: 40,
														bgcolor: "#e3f2fd",
														color: "#1976d2",
														p: 0.5,
														borderRadius: 1,
														textAlign: "center",
														fontWeight: "bold",
														fontSize: "0.85rem",
													}}
												>
													{`{{${index + 1}}}`}
												</Box>

												<Select
													value={v.value}
													onChange={(e) => updateVariable(index, "value", e.target.value)}
													size="small"
													fullWidth
													displayEmpty
													disabled={isSending}
													error={!v.value}
												>
													<MenuItem value="" disabled>
														Selecione a Coluna da Planilha...
													</MenuItem>
													{fields.map((f) => (
														<MenuItem key={f} value={f}>
															{f}
														</MenuItem>
													))}
												</Select>

												<IconButton
													size="small"
													onClick={() => removeVariable(index)}
													disabled={isSending}
													color="error"
													title="Remover Variável"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Box>
										))}
									</Box>
								</Box>
							</Box>
						</Grid>
					</Grid>

					{/* Controles de Disparo */}
					<Box className="border p-4 rounded shadow-sm bg-white mb-4">
						<Typography variant="h6" gutterBottom color="primary">
							4. Configuração de Disparo
						</Typography>
						<Box display="flex" gap={2} flexWrap="wrap">
							<TextField
								label="Delay Mín (s)"
								type="number"
								size="small"
								value={delayMin}
								onChange={(e) => setDelayMin(Number(e.target.value))}
								sx={{ width: 120 }}
							/>
							<TextField
								label="Delay Máx (s)"
								type="number"
								size="small"
								value={delayMax}
								onChange={(e) => setDelayMax(Number(e.target.value))}
								sx={{ width: 120 }}
							/>
							<TextField
								label="Pausar a cada (msgs)"
								type="number"
								size="small"
								value={batchSize}
								onChange={(e) => setBatchSize(Number(e.target.value))}
								sx={{ width: 150 }}
								disabled={isSending}
							/>
							<TextField
								label="Tempo Pausa (min)"
								type="number"
								size="small"
								value={batchPauseMin}
								onChange={(e) => setBatchPauseMin(Number(e.target.value))}
								sx={{ width: 150 }}
								disabled={isSending}
							/>
							<TextField
								label="Máx Erros"
								type="number"
								size="small"
								value={errorLimit}
								onChange={(e) => setErrorLimit(Number(e.target.value))}
								sx={{ width: 120 }}
								disabled={isSending}
							/>
						</Box>

						<Box mt={2}>
							<Typography variant="body1" fontWeight="bold">
								Status: {isWaiting ? <span className="text-orange-500">{status}</span> : status}
							</Typography>
							{loading && <LinearProgress sx={{ mt: 1 }} />}
						</Box>

						<Box mt={3} display="flex" gap={2}>
							<Button
								variant="contained"
								color="primary"
								onClick={sendMessages}
								disabled={isSending || loading}
								size="large"
							>
								{isSending ? "Enviando..." : "Iniciar Disparo Oficial"}
							</Button>

							{isSending && (
								<Button variant="contained" color="error" onClick={cancelSending}>
									Parar Envio
								</Button>
							)}

							<Button variant="outlined" color="success">
								Exportar Relatório
							</Button>
						</Box>
					</Box>

					{/* Tabela de Preview */}
					{contacts.length > 0 && (
						<Paper sx={{ mt: 3 }}>
							<Typography variant="h6" p={2}>
								Contatos
							</Typography>

							<TableContainer>
								<Table>
									<TableHead className="color-table">
										<TableRow>
											{fields.map((col) => (
												<TableCell key={col} style={{ color: "#fff" }}>
													{col.toUpperCase()}
												</TableCell>
											))}
											<TableCell style={{ color: "#fff" }}>Status</TableCell>
										</TableRow>
									</TableHead>

									{(loading || isSending) && (
										<TableRow>
											<TableCell colSpan={fields.length + 1} sx={{ p: 0 }}>
												<LinearProgress color="primary" />
											</TableCell>
										</TableRow>
									)}

									<TableBody>
										{contacts
											.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
											.map((row, index) => {
												const realIndex = page * rowsPerPage + index;
												return (
													<TableRow key={realIndex}>
														{fields.map((field) => (
															<TableCell key={field}>
																<TextField
																	value={row[field] || ""}
																	onChange={(e) =>
																		handleChange(realIndex, field, e.target.value)
																	}
																	size="small"
																	fullWidth
																/>
															</TableCell>
														))}

														<Tooltip
															title={row.errorDetail || ""}
															arrow
															placement="top"
															disableHoverListener={!row.errorDetail}
														>
															<TableCell
																onClick={() => toggleStatus(realIndex)}
																sx={{
																	cursor: "pointer",
																	color:
																		row.status === "📤 Pendente Envio"
																			? "primary.main"
																			: row.status === "🚫 Não enviar"
																				? "text.secondary"
																				: row.status.includes("Enviado")
																					? "success.main"
																					: "error.main",
																}}
															>
																{row.status}
															</TableCell>
														</Tooltip>
													</TableRow>
												);
											})}
									</TableBody>
								</Table>
							</TableContainer>

							<TablePagination
								component="div"
								count={contacts.length}
								page={page}
								onPageChange={(e, p) => setPage(p)}
								rowsPerPage={rowsPerPage}
								onRowsPerPageChange={(e) => {
									setRowsPerPage(parseInt(e.target.value, 10));
									setPage(0);
								}}
								labelRowsPerPage="Linhas por página"
							/>
						</Paper>
					)}
				</Box>
			</div>
			{/* Componente de Upload (Invisível/Modal) controlado por evento */}
			<UploadPhotoV2Image
				ident={"upload-whatsapp"}
				nameImg={"header.png"}
				id={"header"}
				onChange={(data) => {
					// O componente retorna { url: ... }
					if (data && data.url) {
						// Remove timestamp se vier e seta preview
						setImagePreview(data.url);
						toast.success("Imagem carregada!");
					}
				}}
				type={"card"}
			/>
			<Footer />
		</>
	);
};

export default WhatsAppSenderOfficial;
